import jsPDF from 'jspdf';
import type { CredentialType } from '../fetchHooks/useMyPasswords';

export const exportPasswordsToPDF = (passwords: CredentialType[], userName = '') => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = 210; // A4 width in mm
  const pageHeight = 297; // A4 height in mm
  const margin = 20;
  const lineHeight = 8;
  const startY = margin;
  let currentY = startY;

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredHeight: number) => {
    if (currentY + requiredHeight > pageHeight - margin) {
      pdf.addPage();
      currentY = margin;
      return true;
    }
    return false;
  };

  // Title
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  const title = `Passord Eksport${userName ? ` - ${userName}` : ''}`;
  pdf.text(title, margin, currentY);
  currentY += lineHeight * 2;

  // Date
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const exportDate = new Date().toLocaleDateString('nb-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  pdf.text(`Eksportert: ${exportDate}`, margin, currentY);
  currentY += lineHeight * 2;

  // Warning
  pdf.setFontSize(10);
  pdf.setTextColor(255, 0, 0); // Red color
  pdf.text('ADVARSEL: Dette dokumentet inneholder passord i klartekst!', margin, currentY);
  pdf.text('Lagre dette dokumentet på en trygg plass og ikke del det med andre.', margin, currentY + lineHeight);
  currentY += lineHeight * 3;
  pdf.setTextColor(0, 0, 0); // Reset to black

  // Table headers
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const headers = ['Nettsted/Tjeneste', 'Brukernavn', 'Passord', 'Kommentar', 'Lagt til'];
  const colWidths = [40, 35, 35, 50, 30];
  const headerY = currentY;

  // Draw header background
  pdf.setFillColor(240, 240, 240);
  pdf.rect(margin, headerY - 6, pageWidth - margin * 2, 8, 'F');

  // Draw header text
  let xPos = margin;
  headers.forEach((header, index) => {
    pdf.text(header, xPos, headerY);
    xPos += colWidths[index];
  });

  currentY += lineHeight * 1.5;

  // Draw line under header
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, currentY, pageWidth - margin, currentY);
  currentY += lineHeight;

  // Password entries
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');

  passwords.forEach((password, index) => {
    checkPageBreak(lineHeight * 2);

    const rowData = [
      password.website || '-',
      password.username || '-',
      password.password || '-',
      password.comment || '-',
      password.createdAt ? new Date(password.createdAt).toLocaleDateString('nb-NO') : '-',
    ];

    // Draw row background for better readability
    if (index % 2 === 0) {
      pdf.setFillColor(250, 250, 250);
      pdf.rect(margin, currentY - 5, pageWidth - margin * 2, lineHeight * 1.2, 'F');
    }

    xPos = margin;
    rowData.forEach((cell, cellIndex) => {
      // Truncate long text to fit column
      const maxWidth = colWidths[cellIndex] - 2;
      let text = cell.toString();
      if (pdf.getTextWidth(text) > maxWidth) {
        // Truncate text to fit
        while (pdf.getTextWidth(text + '...') > maxWidth && text.length > 0) {
          text = text.slice(0, -1);
        }
        text = text + '...';
      }
      pdf.text(text, xPos, currentY);
      xPos += colWidths[cellIndex];
    });

    currentY += lineHeight * 1.2;

    // Draw line between rows
    pdf.setDrawColor(230, 230, 230);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += lineHeight * 0.3;
  });

  // Footer
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Side ${i} av ${totalPages} - Generert ${exportDate}`, margin, pageHeight - 10);
    pdf.setTextColor(0, 0, 0);
  }

  // Save the PDF
  const fileName = `passord-eksport-${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
};
